"use client";

import { useState } from "react";
import Link from "@/components/locale-link";
import {
  HIGHLIGHT_TONES,
  HighlightedText,
  type HighlightTone,
} from "@/components/tool-kit/highlighted-text";
import {
  CopyButton,
  Notice,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  HINT,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  analyzePrompt,
  buildChecklist,
  type ChecklistStep,
  type HighlightKind,
  type PromptAnalysis,
  promptHighlights,
} from "@/lib/tool-kit/essay/prompt-analyzer";
import {
  DIRECTIVE_DEFINITIONS,
  DIRECTIVE_LABELS,
  DIRECTIVE_TASKS,
} from "@/lib/tool-kit/essay/prompt-directives";
import {
  type FindingSet,
  requirementSets,
  stepText,
} from "@/lib/tool-kit/essay/prompt-text";

/** English strings. A translated content file overrides these through `ui`. */
export const ESSAY_PROMPT_ANALYZER_STRINGS = {
  promptLabel: "Your assignment prompt",
  promptPlaceholder:
    "Paste the full prompt, including any requirements about length, sources, citation style, formatting, and due dates.",
  promptHint:
    "Everything stays in your browser — the prompt is read on this page and never uploaded.",
  tryExample: "Try an example",
  clear: "Clear",
  emptyNotice:
    "Paste a prompt to see its task words, its requirements, and a step-by-step plan.",
  markedHeading: "Your prompt, marked up",
  markedLabel: "Your prompt with task words and requirements highlighted",
  legend: {
    directive: "Task words",
    length: "Length and formatting",
    sources: "Sources",
    style: "Citation style",
    date: "Dates",
  },
  tasksHeading: "one: {count} task word|other: {count} task words",
  noTasks:
    "No task words found. The prompt may ask its question indirectly — find the main question and decide what kind of answer it needs.",
  means: "Means:",
  mustDo: "Your response should:",
  foundAs: "Found as “{text}”",
  appears: "one: appears once|other: appears {count} times",
  requirementsHeading: "Requirements found",
  groups: {
    length: "Length",
    sources: "Sources",
    style: "Citation style",
    dates: "Dates and deadlines",
    format: "Formatting and submission",
  },
  noneFound: "None found",
  quoted: "“{text}”",
  labelColon: "{label}:",
  lengthRange: "{min}–{max}{unit}",
  lengthMin: "at least {min}{unit}",
  lengthMax: "no more than {max}{unit}",
  lengthAbout: "about {n}{unit}",
  unitWords: "one: word|other: words",
  unitPages: "one: page|other: pages",
  unitParagraphs: "one: paragraph|other: paragraphs",
  unitCharacters: "one: character|other: characters",
  unitMinutes: "one: minute|other: minutes",
  sourceKinds: {
    count: "Number",
    type: "Type",
    ban: "Not allowed",
    recency: "How recent",
  },
  styleEdition: "{name}, edition {edition}",
  styleGuideLink: "Open the {name} guide",
  dateKinds: {
    draft: "Draft due",
    final: "Final version due",
    deadline: "Deadline",
    date: "Date mentioned",
  },
  dateWithTime: "{date}, {time}",
  formatLabels: {
    doubleSpaced: "Double-spaced",
    singleSpaced: "Single-spaced",
    spacing15: "1.5 line spacing",
    fontSize: "Font size",
    font: "Font",
    margins: "Margins",
    titlePage: "Title page",
    runningHead: "Running head",
    pageNumbers: "Page numbers",
    headings: "Section headings",
    mlaHeading: "MLA heading",
    abstract: "Abstract",
    referencesPage: "Works Cited, references, or bibliography",
    footnotes: "Footnotes or endnotes",
    inText: "In-text citations",
    fileFormat: "File format",
    submission: "Where to submit",
    tableOfContents: "Table of contents",
    appendix: "Appendix",
    wordCountStatement: "Word count statement",
  },
  termsHeading: "Key terms in the prompt",
  termsIntro:
    "The content words and names the prompt leans on — define or explain each one in your answer.",
  noTerms: "No distinctive terms left once the instructions are set aside.",
  planHeading: "Your step-by-step plan",
  copyPlan: "Copy plan",
  copied: "Copied!",
  steps: {
    read: "Read the whole prompt twice and underline every task word.",
    directive: "{label}: {task}",
    terms: "Define or explain the key terms: {detail}.",
    sources: "Gather sources that meet this requirement: “{detail}”.",
    style:
      "Cite every source in {detail} style, both in the text and in the list of sources.",
    length:
      "Plan your draft to this length — “{detail}” — and budget words for each section.",
    format: "Format the paper as required: {detail}.",
    deadlineDraft: "Draft due {detail}. Leave time to revise after feedback.",
    deadlineFinal:
      "Final version due {detail}. Set yourself an earlier date for a full draft.",
    deadline:
      "Deadline: {detail}. Set yourself an earlier date for a full draft.",
    thesis: "Write a working thesis that answers every task word above.",
    final:
      "Before you submit, reread the prompt and check off each requirement.",
  },
  plannerLink: "Budget the words with the Essay Word Count Planner",
  limitsHeading: "What this analyzer can and can't do",
  limits:
    "It matches words and patterns; it does not understand the prompt. It can miss a requirement phrased in an unusual way or flag a word used in another sense, and it cannot tell which task matters most. Check every finding against the prompt itself, and ask your instructor when something is ambiguous.",
  example:
    "To what extent did the New Deal end the Great Depression? In a 1,500–2,000-word essay, evaluate the effectiveness of three New Deal programs, using at least five scholarly sources, including at least two primary sources. Cite your sources in Chicago style (notes and bibliography). Your paper should be double-spaced in 12-point Times New Roman with 1-inch margins and page numbers. Submit a PDF on Canvas by 11:59 p.m. on Friday, March 13.",
  directiveLabels: DIRECTIVE_LABELS,
  directiveDefinitions: DIRECTIVE_DEFINITIONS,
  directiveTasks: DIRECTIVE_TASKS,
} as const;

type Strings = typeof ESSAY_PROMPT_ANALYZER_STRINGS;

const TONE_OF: Record<HighlightKind, HighlightTone> = {
  directive: "amber",
  length: "sky",
  format: "sky",
  sources: "emerald",
  style: "violet",
  date: "rose",
};

const LEGEND: readonly (keyof Strings["legend"])[] = [
  "directive",
  "length",
  "sources",
  "style",
  "date",
];

function FindingGroup({ set, strings }: { set: FindingSet; strings: Strings }) {
  return (
    <div className={`${CARD} space-y-2 ${set.wide ? "md:col-span-2" : ""}`}>
      <h3 className="font-semibold text-foreground text-sm">{set.title}</h3>
      {set.items.length === 0 ? (
        <p className="text-muted-foreground text-sm italic">
          {strings.noneFound}
        </p>
      ) : (
        <ul className="space-y-1.5 text-foreground text-sm">
          {set.items.map((item) => (
            <li key={item.key}>
              {item.label ? (
                <span className="font-medium">
                  {t(strings.labelColon, { label: item.label })}{" "}
                </span>
              ) : null}
              {item.text}
              {item.quote ? (
                <span className="text-muted-foreground">
                  {" "}
                  {t(strings.quoted, { text: item.quote })}
                </span>
              ) : null}
              {item.link ? (
                <>
                  {" · "}
                  <Link
                    className="text-primary underline underline-offset-4"
                    href={item.link.href}
                  >
                    {item.link.text}
                  </Link>
                </>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Requirements({
  analysis,
  strings,
}: {
  analysis: PromptAnalysis;
  strings: Strings;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {requirementSets(analysis, strings, locale, fmt).map((set) => (
        <FindingGroup key={set.title} set={set} strings={strings} />
      ))}
    </div>
  );
}

function Plan({
  steps,
  strings,
}: {
  steps: readonly ChecklistStep[];
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const [done, setDone] = useState<ReadonlySet<number>>(new Set());
  const texts = steps.map((step) => stepText(step, strings));
  const toggle = (index: number) =>
    setDone((previous) => {
      const next = new Set(previous);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className={WIDGET_H2}>{strings.planHeading}</h2>
        <CopyButton
          copiedLabel={strings.copied}
          label={strings.copyPlan}
          small
          text={texts
            .map((text, index) => `${fmt(index + 1)}. ${text}`)
            .join("\n")}
        />
      </div>
      <ol className="space-y-2">
        {texts.map((text, index) => (
          // Steps are positional: two sources steps can quote the same words.
          // biome-ignore lint/suspicious/noArrayIndexKey: positional steps
          <li className="flex gap-3" key={index}>
            <input
              checked={done.has(index)}
              className="mt-1 accent-primary"
              id={`epa-step-${index}`}
              onChange={() => toggle(index)}
              type="checkbox"
            />
            <label
              className={
                done.has(index)
                  ? "text-muted-foreground text-sm leading-relaxed line-through"
                  : "text-foreground text-sm leading-relaxed"
              }
              htmlFor={`epa-step-${index}`}
            >
              <span className="font-medium">{fmt(index + 1)}.</span> {text}
              {steps[index]?.id === "length" ? (
                <>
                  {" "}
                  <Link
                    className="text-primary underline underline-offset-4"
                    href="/tools/essay-word-budget-calculator"
                  >
                    {strings.plannerLink}
                  </Link>
                </>
              ) : null}
            </label>
          </li>
        ))}
      </ol>
    </section>
  );
}

export default function EssayPromptAnalyzerWidget() {
  const strings = useToolUi(ESSAY_PROMPT_ANALYZER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [prompt, setPrompt] = useState("");

  const hasPrompt = prompt.trim() !== "";
  const analysis = hasPrompt ? analyzePrompt(prompt) : null;
  const matches = analysis
    ? promptHighlights(analysis).map((mark) => ({
        start: mark.start,
        end: mark.end,
        category: mark.kind,
        title: strings.legend[mark.kind === "format" ? "length" : mark.kind],
      }))
    : [];

  return (
    <>
      <div className={`${CARD} space-y-4`}>
        <TextAreaField
          hint={strings.promptHint}
          id="epa-prompt"
          label={strings.promptLabel}
          onChange={setPrompt}
          placeholder={strings.promptPlaceholder}
          prose
          rows={7}
          value={prompt}
        />
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setPrompt(strings.example)}
            type="button"
          >
            {strings.tryExample}
          </button>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setPrompt("")}
            type="button"
          >
            {strings.clear}
          </button>
        </div>
      </div>

      {analysis ? (
        <>
          <section className="space-y-3">
            <h2 className={WIDGET_H2}>{strings.markedHeading}</h2>
            <ul className="flex flex-wrap gap-2">
              {LEGEND.map((kind) => (
                <li
                  className={`${HIGHLIGHT_TONES[TONE_OF[kind]]} text-xs`}
                  key={kind}
                >
                  {strings.legend[kind]}
                </li>
              ))}
            </ul>
            <HighlightedText
              label={strings.markedLabel}
              matches={matches}
              text={prompt}
              tones={TONE_OF}
            />
          </section>

          <section className="space-y-3">
            {/* The task-word count is the live part; the marked-up prompt above
                would re-read the whole assignment on every keystroke. */}
            <h2 aria-live="polite" className={WIDGET_H2}>
              {tPlural(
                strings.tasksHeading,
                locale,
                analysis.directives.length,
                {
                  count: fmt(analysis.directives.length),
                }
              )}
            </h2>
            {analysis.directives.length === 0 ? (
              <Notice>{strings.noTasks}</Notice>
            ) : (
              <div className="grid gap-3 md:grid-cols-2">
                {analysis.directives.map((hit) => (
                  <div className={`${CARD} space-y-2`} key={hit.id}>
                    <h3 className={WIDGET_H3}>
                      {strings.directiveLabels[hit.id]}
                    </h3>
                    <p className="text-foreground text-sm">
                      <span className="font-medium">{strings.means}</span>{" "}
                      {strings.directiveDefinitions[hit.id]}
                    </p>
                    <p className="text-foreground text-sm">
                      <span className="font-medium">{strings.mustDo}</span>{" "}
                      {strings.directiveTasks[hit.id]}
                    </p>
                    <p className={HINT}>
                      {t(strings.foundAs, { text: hit.text })} ·{" "}
                      {tPlural(strings.appears, locale, hit.spans.length, {
                        count: fmt(hit.spans.length),
                      })}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="space-y-3">
            <h2 className={WIDGET_H2}>{strings.requirementsHeading}</h2>
            <Requirements analysis={analysis} strings={strings} />
          </section>

          <section className="space-y-3">
            <h2 className={WIDGET_H2}>{strings.termsHeading}</h2>
            {analysis.terms.length === 0 ? (
              <Notice>{strings.noTerms}</Notice>
            ) : (
              <>
                <p className="text-muted-foreground text-sm">
                  {strings.termsIntro}
                </p>
                <ul className="flex flex-wrap gap-2">
                  {analysis.terms.map((term) => (
                    <li
                      className="rounded-full border border-border bg-card px-3 py-1 text-foreground text-sm"
                      key={term.text}
                    >
                      {term.text}
                      {term.count > 1 ? (
                        <span className="ms-1.5 text-muted-foreground text-xs">
                          ×{fmt(term.count)}
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>

          <Plan
            key={prompt}
            steps={buildChecklist(analysis)}
            strings={strings}
          />
        </>
      ) : (
        <Notice>{strings.emptyNotice}</Notice>
      )}

      <section className="space-y-2">
        <h2 className={WIDGET_H3}>{strings.limitsHeading}</h2>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {strings.limits}
        </p>
      </section>
    </>
  );
}
